import { gql, useMutation } from '@apollo/client';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { Skeleton } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { UserVerificationVisual } from './userVerification.visual';

const VERIFY_USER = gql`
  mutation verifyUserAndAddToAccount($input: VerifyUserAndAddToAccountInput!) {
    verifyUserAndAddToAccount(input: $input) {
      isVerified
    }
  }
`;

export const UserVerification = ({
  options,
  userId,
  onNext,
  stepLoading,
  grid = false,
  updateMode = false,
  workflowCompletion,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  workflowCompletion?: any;
}) => {
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const [usedAttempts, setUsedAttempts] = useState(
    workflowData?.usedVerificationAttempts ?? 0,
  );
  const [userData, setUserData] = useState<any>({
    firstName: '',
    lastName: '',
    primaryEmail: '',
    dateOfBirth: '',
  });
  const [isMaxAttemptsExceeded, setIsMaxAttemptsExceeded] = useState(
    workflowData?.userAllowedNumberOfAttempts &&
      workflowData?.userAllowedNumberOfAttempts <
        (workflowData?.usedVerificationAttempts ?? 0),
  );

  useEffect(() => {
    if (userId && !workflowData.currentUserId) {
      setWorkflowData({
        ...workflowData,
        currentUserId: userId,
        userAllowedNumberOfAttempts: options?.numberOfAttempts?.number ?? 1,
      });
    }
  }, [userId, workflowData]);

  useEffect(() => {
    setWorkflowData({
      ...workflowData,
      userAllowedNumberOfAttempts: options?.numberOfAttempts?.number ?? 1,
    });
  }, []);

  const [verifyUserMutation, { loading: verifyUserLoading }] =
    useMutation(VERIFY_USER);

  const onVerification = async () => {
    // if user is already verified, skip verification
    if (workflowData?.isUserVerified) {
      onNext();
      return;
    }

    const updatedAttempts = (workflowData?.usedVerificationAttempts ?? 0) + 1;
    await setWorkflowData({
      ...workflowData,
      usedVerificationAttempts: updatedAttempts,
    });
    setUsedAttempts(updatedAttempts);
    if (updatedAttempts > workflowData?.userAllowedNumberOfAttempts) {
      setIsMaxAttemptsExceeded(true);
      return;
    }

    const enabledAttributes = [
      options?.firstName?.enabled && 'firstName',
      options?.lastName?.enabled && 'lastName',
      options?.primaryEmail?.enabled && 'primaryEmail',
      options?.dateOfBirth?.enabled && 'dateOfBirth',
    ].filter(Boolean);

    const isInvitationToBeSent =
      (options?.inviteUserToAccount && workflowData?.currentAccountId) ?? false;

    // call verification endpoint
    const result = await verifyUserMutation({
      variables: {
        input: {
          userId,
          accountId: isInvitationToBeSent
            ? workflowData.currentAccountId
            : undefined,
          enabledAttributes,
          firstName: enabledAttributes.includes('firstName')
            ? userData.firstName
            : undefined,
          lastName: enabledAttributes.includes('lastName')
            ? userData.lastName
            : undefined,
          primaryEmail: enabledAttributes.includes('primaryEmail')
            ? userData.primaryEmail
            : undefined,
          dateOfBirth: enabledAttributes.includes('dateOfBirth')
            ? userData.dateOfBirth
            : undefined,
        },
      },
    });
    if (result.data.verifyUserAndAddToAccount.isVerified) {
      await setWorkflowData({
        ...workflowData,
        isUserVerified: true,
      });
      onNext();
    }
  };

  return (
    <>
      {stepLoading || verifyUserLoading ? (
        <>
          <Skeleton
            width='200px'
            height='33px'
            variant='text'
            data-testid='custom-field-skeleton'
          />
          <Skeleton
            width='100px'
            height='19px'
            variant='text'
            data-testid='custom-field-skeleton'
          />
        </>
      ) : (
        <UserVerificationVisual
          options={options}
          userData={userData}
          updateUser={setUserData}
          continueFunc={onVerification}
          loading={stepLoading || verifyUserLoading}
          grid={grid}
          updateMode={updateMode}
          workflowCompletion={workflowCompletion}
          isMaxAttemptsExceeded={!!isMaxAttemptsExceeded}
          allowedAttemps={workflowData?.userAllowedNumberOfAttempts || 0}
          failedVerificationAttemps={usedAttempts}
        />
      )}
    </>
  );
};

export default UserVerification;
